import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from 'react-query';
import Select from 'react-select';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import OutlinedButton from '../../components/Buttons/OutlinedButton';
import ConfirmActionModal from '../../modals/ConfirmActionModal';
import TrainingPathService from '../../services/TrainingPathService';
import OrganizationService from '../../services/OrganizationService';
import UserService from '../../services/UserService';
import {
  getTrainingPathStatusLabel,
  getTrainingPathStatusToken,
} from '../../constants/main';
import Tooltip from '../../components/Tooltip';
import { useUiStore } from '../../store';
import { UiContext } from '../../context/UiContext';
import closeIcon from '../../assets/images/closeIcon.svg';
import checkIcon from '../../assets/images/view-meeting/checkmark.svg';

import classes from './styles.module.scss';

const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 12,
    borderColor: 'var(--text)',
    minHeight: 56,
    boxShadow: 'none',
    fontSize: '20px',
    lineHeight: '28px',
    paddingLeft: '4px',
    ':hover': {
      borderColor: 'var(--primary)',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'rgba(122, 178, 255, 0.18)' : undefined,
    color: '#1b1c1f',
    fontSize: '20px',
    lineHeight: '28px',
    padding: '12px 16px',
    cursor: 'pointer',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    marginTop: '4px',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    padding: '4px',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgba(122, 178, 255, 0.24)',
    borderRadius: '8px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#1b1c1f',
    fontSize: '18px',
    lineHeight: '26px',
    padding: '4px 8px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    ':hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      color: '#ef4444',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#666666',
    fontSize: '20px',
  }),
};

export default function TrainingPathTraineesPage() {
  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );
  const { notifyError, notifySuccess } = useContext(UiContext);
  const [selectionMap, setSelectionMap] = useState({});
  const [isAssigningUserId, setIsAssigningUserId] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [isRemovalModalVisible, setIsRemovalModalVisible] = useState(false);

  useEffect(() => {
    setBreadcrumbsOptions([
      { label: 'Training Paths', to: '/training-paths' },
      { label: 'Manage Trainees', to: '/training-paths/trainees' },
    ]);
  }, [setBreadcrumbsOptions]);

  const { data: currentUser } = useQuery('me', UserService.getMe);
  const organizationId = currentUser?.organizationId;

  const { data: organizationUsers = [] } = useQuery(
    ['organization-users', organizationId],
    () => OrganizationService.getOrganizationUsers(organizationId),
    {
      enabled: Boolean(organizationId),
    }
  );

  const {
    data: trainingPaths = [],
    refetch: refetchTrainingPaths,
  } = useQuery(
    ['training-paths', 'manage-trainees'],
    () =>
      TrainingPathService.getTrainingPaths({
        page: 1,
        limit: 200,
        acl: 'admin',
      }),
    {
      select: (response) => response?.rows || [],
    }
  );

  const trainingPathOptions = useMemo(() => {
    return trainingPaths.map((path) => ({
      value: path.id,
      label: path.name,
    }));
  }, [trainingPaths]);

  const assignmentsByUser = useMemo(() => {
    const map = {};
    trainingPaths.forEach((path) => {
      (path.Assignments || []).forEach((assignment) => {
        if (!map[assignment.userId]) {
          map[assignment.userId] = [];
        }
        map[assignment.userId].push({
          assignmentId: assignment.id,
          pathId: path.id,
          pathName: path.name,
          status: assignment.status,
          progressPercent: assignment.progressPercent || 0,
        });
      });
    });
    return map;
  }, [trainingPaths]);

  useEffect(() => {
    if (!organizationUsers.length || !trainingPathOptions.length) {
      return;
    }

    setSelectionMap((prev) => {
      const next = { ...prev };
      organizationUsers.forEach((user) => {
        if (!next[user.id]) {
          // Initialize with empty selection (assigned paths are shown as badges)
          next[user.id] = [];
        }
      });
      return next;
    });
  }, [assignmentsByUser, organizationUsers, trainingPathOptions]);

  const handleSelectionChange = (userId, options) => {
    setSelectionMap((prev) => ({
      ...prev,
      [userId]: options,
    }));
  };

  const handleAssign = async (userId) => {
    const selectedOptions = selectionMap[userId] || [];
    const currentAssignments = assignmentsByUser[userId] || [];
    const assignedPathIds = new Set(
      currentAssignments
        .filter((a) => a.status !== 'canceled')
        .map((a) => a.pathId)
    );
    const newPathIds = selectedOptions
      .map((option) => option.value)
      .filter((id) => !assignedPathIds.has(id));

    if (!newPathIds.length) {
      notifyError('Select at least one new training path to assign.');
      return;
    }

    setIsAssigningUserId(userId);
    try {
      await Promise.all(
        newPathIds.map((pathId) =>
          TrainingPathService.assignUsers(pathId, [userId])
        )
      );
      notifySuccess('Training paths assigned successfully.');
      setSelectionMap((prev) => ({
        ...prev,
        [userId]: [], // Clear selection after assignment
      }));
      refetchTrainingPaths();
    } catch (error) {
      console.error(error);
      notifyError(
        error?.response?.data?.message ||
          'Unable to assign training paths. Please try again.'
      );
    } finally {
      setIsAssigningUserId(null);
    }
  };

  const handleBadgeRemove = useCallback((userId, assignment) => {
    if (assignment.status === 'canceled') {
      return; // Already canceled, shouldn't happen but just in case
    }
    setPendingRemoval({ userId, assignment });
    setIsRemovalModalVisible(true);
  }, []);

  const handleRemovalConfirm = useCallback(async () => {
    if (!pendingRemoval) {
      return;
    }

    try {
      await TrainingPathService.updateAssignmentStatus({
        assignmentId: pendingRemoval.assignment.assignmentId,
        status: 'canceled',
      });
      notifySuccess('Training path assignment canceled successfully.');
      setIsRemovalModalVisible(false);
      setPendingRemoval(null);
      refetchTrainingPaths();
    } catch (error) {
      console.error(error);
      notifyError(
        error?.response?.data?.message ||
          'Unable to cancel assignment. Please try again.'
      );
    }
  }, [pendingRemoval, notifyError, notifySuccess, refetchTrainingPaths]);

  const handleRemovalClose = useCallback(() => {
    setIsRemovalModalVisible(false);
    setPendingRemoval(null);
  }, []);


  return (
    <Layout hasUserBlock>
      <div className={classes.TraineesPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
              <Breadcrumbs
                breadcrumbsOptions={[
                  { label: 'Training Paths', to: '/training-paths' },
                  { label: 'Manage Trainees', to: '/training-paths/trainees' },
                ]}
              />
              <p>Assign existing users to one or more training paths</p>
            </div>


          <div className={classes.tableCard}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Training Paths</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {organizationUsers.map((user) => {
                  const selectionValue = selectionMap[user.id] || [];
                  const userAssignments = assignmentsByUser[user.id] || [];
                  const activeAssignments = userAssignments.filter(
                    (a) => a.status !== 'canceled'
                  );
                  const assignedPathIds = new Set(
                    activeAssignments.map((a) => a.pathId)
                  );
                  const availableOptions = trainingPathOptions.filter(
                    (option) => !assignedPathIds.has(option.value)
                  );
                  const hasNewSelection = selectionValue.length > 0;

                  return (
                    <tr key={user.id}>
                      <td>
                        <div className={classes.userCell}>
                          <div className={classes.avatar}>
                            {user.name?.[0] || user.email?.[0]}
                          </div>
                          <div>
                            <p className={classes.userName}>{user.name}</p>
                            <span className={classes.userTitle}>
                              {user.title || 'No title'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={classes.trainingPathsCell}>
                          {activeAssignments.length > 0 && (
                            <div className={classes.badgesContainer}>
                              {activeAssignments.map((assignment) => {
                                const isCompleted = assignment.status === 'completed';
                                const statusLabel = getTrainingPathStatusLabel(assignment.status);
                                return (
                                  <Tooltip
                                    key={assignment.assignmentId}
                                    text={`Status: ${statusLabel}`}
                                    position="left"
                                    width={180}
                                  >
                                    <div
                                      className={classes.badge}
                                      data-status={getTrainingPathStatusToken(assignment.status)}
                                    >
                                      {isCompleted && (
                                        <span className={classes.badgeCheckmark}>
                                          <img src={checkIcon} alt="" />
                                        </span>
                                      )}
                                      <span className={classes.badgeLabel}>
                                        {assignment.pathName}
                                      </span>
                                      {!isCompleted && (
                                        <button
                                          type="button"
                                          className={classes.badgeClose}
                                          onClick={() => handleBadgeRemove(user.id, assignment)}
                                          aria-label={`Remove ${assignment.pathName}`}
                                        >
                                          <img src={closeIcon} alt="" />
                                        </button>
                                      )}
                                    </div>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          )}
                          <div className={classes.selectWrapper}>
                        <Select
                          isMulti
                          value={selectionValue}
                          onChange={(options) =>
                            handleSelectionChange(user.id, options)
                          }
                              options={availableOptions}
                          classNamePrefix="trainee-select"
                          styles={selectStyles}
                          placeholder="Select training paths"
                        />
                          </div>
                        </div>
                      </td>
                      <td>
                        <OutlinedButton
                          isDisabled={
                            !hasNewSelection || isAssigningUserId === user.id
                          }
                          onClick={() => handleAssign(user.id)}
                        >
                          {isAssigningUserId === user.id
                            ? 'Assigning...'
                            : 'Assign Training Path(s)'}
                        </OutlinedButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmActionModal
        title="Remove training path?"
        message={
          pendingRemoval
            ? (() => {
                const user = organizationUsers.find((u) => u.id === pendingRemoval.userId);
                const userName = user?.name || 'this user';
                const progressPercent = Math.round(
                  pendingRemoval.assignment.progressPercent || 0
                );
                if (progressPercent > 0) {
                  return `User ${userName} has already completed <strong>${progressPercent}%</strong> of this training path. This will cancel the assignment and change the status to Canceled.`;
                }
                return `This will cancel the assignment for ${userName}. The assignment status will be changed to Canceled.`;
              })()
            : ''
        }
        show={isRemovalModalVisible}
        handleClose={handleRemovalClose}
        onConfirm={handleRemovalConfirm}
      />
    </Layout>
  );
}

